import { ForwardedRef, MutableRefObject } from 'react';

import { BUILD, IS_PRODUCTION } from '@srgssr/rio-common';
import { Maybe } from '@srgssr/rio-shared';

import { PLATFORM_NAME } from './constants';
import { USER_AGENT } from './userAgent';

/* eslint-disable no-param-reassign */
export const setComponentRefs =
  <T>(ref: MutableRefObject<T | null>, forwardedRef: ForwardedRef<T>) =>
  (el: T) => {
    ref.current = el;
    if (typeof forwardedRef === 'function') forwardedRef(el);
    else if (forwardedRef) forwardedRef.current = el;
  };
/* eslint-enable no-param-reassign */

export const hasIndex = <T>(item: T | undefined): item is Extract<T, { index: number }> =>
  (item as { index: number } | undefined)?.index != null;

export const getAcronym = (str: string) =>
  str
    .split(' ')
    .map((item) => item[0])
    .join('')
    .replace(/[^A-Za-z0-9]/g, '');

/**
 * Used to force only two letters from a name acronym (removing middlenames and longer names)
 * @param str name
 */
export const getTwoLetterNameAcronym = (str: string) => {
  const acronym = getAcronym(str);
  if (acronym && acronym.length > 2) {
    return acronym
      .split('')
      .filter((_, i) => i % 2 === 0)
      .slice(0, 2)
      .join('');
  }
  return acronym;
};

/**
 * Check if string starts with http or https
 * @param url url
 * @returns boolean
 */
export const isHttpUrl = (url: string) => url.startsWith('http://') || url.startsWith('https://');

const DURATION_EMPTY_STEP = '00:';
/**
 * Converts seconds to duration in 00:00:00 format
 * @param seconds string
 * @returns
 */
export const secondsToDuration = (seconds: number) => {
  const date = new Date(0);
  date.setSeconds(seconds);
  let duration = date.toISOString().substring(11, 19);
  while (duration.startsWith(DURATION_EMPTY_STEP) && duration.length > 5) {
    duration = duration.slice(DURATION_EMPTY_STEP.length);
  }
  return duration;
};

/**
 * Replace last space by non breaking space if string ends by ! or ?
 * @param str string
 * @returns string
 */
export const replaceNonBreakingSpaces = (str: Maybe<string>) => str?.replace(/\s*(?=!|\?)\s*/g, '\u00A0');

/**
 * Replaces line breaks in string by spaces
 * @param str string
 * @returns string
 */
export const replaceLineBreaksBySpaces = (str: string) => str.replace(/\r?\n|\r/g, ' ');

/**
 * Promise thats resolves after a specified duration
 * @param duration number
 * @returns
 */
export const wait = (duration = 0) =>
  new Promise((resolve) => {
    setTimeout(resolve, duration);
  });

export const debugAlert = (message: string) => {
  if (!IS_PRODUCTION) {
    // eslint-disable-next-line no-alert
    alert(message);
  }
};

const TYPEFORM_BASE_URL = 'https://form.typeform.com/to/ibpIGrGw';

export const getDeviceFormUrl = ({
  autoplay = true,
  backgroundVideoPlayback = false,
  appIdentifier = 'ch.srgssr.playsuisse.tv-web',
  srgUuid = null,
}: {
  autoplay?: boolean;
  backgroundVideoPlayback?: boolean;
  subtitleSettings?: string | null;
  appName?: string;
  appVersion?: string;
  appIdentifier?: string;
  srgUuid?: string | null;
}) => {
  const params = {
    autoplay,
    background_video_playback: backgroundVideoPlayback,
    logged_in: true,
    app_name: `PlaySuisse.${PLATFORM_NAME}`,
    app_version: BUILD.appVersionName,
    os: USER_AGENT.os.name,
    os_version: USER_AGENT.os.version,
    app_identifier: appIdentifier,
    model: USER_AGENT.device.model,
    model_identifier: USER_AGENT.device.type,
    srg_uuid: srgUuid,
  };

  const queryString = Object.entries(params)
    .filter(([_, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join('&');
  return `${TYPEFORM_BASE_URL}#${queryString}`;
};
